/**
 * The day a cycle started, for telling one option from another.
 *
 * Guarded on both sides. An unparseable `startDate` makes
 * `Intl.DateTimeFormat.format` throw `RangeError`, and this runs inside the
 * options memo, so a single bad date would take the whole picker down. The
 * year-only `slice(0, 4)` it replaced could not fail that way.
 *
 * And a cycle she described by month in the past-cycle dialog is stored as
 * `${year}-${month}-01`, so printing a day would show "1 juin 2024" for a
 * precision she never gave. Those keep the year.
 */
export function formatCycleStart(
  startDate: string | null | undefined,
  locale: string,
): string | undefined {
  if (!startDate) return undefined;
  const day = startDate.slice(0, 10);
  const year = startDate.slice(0, 4);
  /*
   * The first of a month keeps its month, not just its year.
   *
   * A cycle she described as "06/2024" in the past-cycle dialog is stored as
   * `2024-06-01`, so printing a day would claim a precision she never gave. But
   * falling all the way back to the year put two cycles a season apart under
   * the same label again. That is the ambiguity this formatting exists to
   * remove, in pickers where choosing wrong attaches a record to the wrong cycle.
   */
  const monthPrecision = day.endsWith("-01");
  const parsed = new Date(`${day}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return year || undefined;
  try {
    return new Intl.DateTimeFormat(locale, {
      ...(monthPrecision ? {} : { day: "numeric" }),
      month: "short",
      year: "numeric",
    }).format(parsed);
  } catch {
    return year || undefined;
  }
}
